import { useRef, useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  BedDouble,
  CalendarDays,
  Car,
  HelpCircle,
  LogOut,
  Menu,
  MessageSquare,
  User,
} from "lucide-react";
import style from "../style/header.module.css";
import TripActionBar from "./TripActionBar";
import { useAuthStore } from "../hooks/useAuthStore";
import { useAppConfigContext } from "../context/AppConfigContext";

const ALL_NAV_LINKS = [
  { label: "Pulo Assistant", to: "/user/travel-assistant", service: "puloAssistants" },
  { label: "Stay", to: "/user/stays", service: "stays" },
  { label: "Rides", to: "/user/rides", service: "rides" },
  { label: "My Trips", to: "/user/trips", service: null },
  { label: "Messages", to: "/user/messages", service: null },
];

function HeaderDash() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleButtonRef = useRef(null);

  const { user, userName, isAuthenticated, actions } = useAuthStore();
  const { config, isServiceEnabled } = useAppConfigContext();
  const navLinks = ALL_NAV_LINKS.filter(
    (link) => !link.service || isServiceEnabled(link.service)
  );

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, []);

  const getUserEmail = () =>
    user?.email || "";

  const getUserAvatar = () => {
    if (!user) return "";
    return (
      user.avatar ||
      (typeof user.profilePicture === "string" ? user.profilePicture : user.profilePicture?.url) ||
      user.photo ||
      user.image ||
      ""
    );
  };

  const profileActions = [
    { label: "Profile", to: "/user/profile", Icon: User },
    { label: "Help Center", to: "/help-center", Icon: HelpCircle },
  ];

  const handleLogout = async () => {
    await actions.logout();
    setIsMenuOpen(false);
    navigate("/auth/login", { replace: true });
  };

  const goTo = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (!parts.length) return "U";
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "U";
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const displayName = userName || "Traveler";
  const avatar = getUserAvatar();
  const email = getUserEmail();

  return (
    <header className="sticky inset-x-0 top-0 z-50 bg-white/95 backdrop-blur ">
      <div className=" flex w-full  items-center justify-between px-4 py-3 sm:px-6">
        {/* Logo */}
        <div className={style.logoH}>
          <NavLink to={isAuthenticated ? "/user/travel-assistant" : "/"}>
            <img
              src={config.logoUrl || "/images/logo.png"}
              alt={config.appName || "Logo"}
              className={style.logo}
            />
          </NavLink>
        </div>

        <nav className="hidden items-center gap-10 text-lg font-medium text-gray-800 md:flex">
          {navLinks.map(({ label, to }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `relative transition-colors hover:text-[#97025C] ${
                  isActive ? "text-[#97025C]" : ""
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{label}</span>
                  <span
                    className={`absolute -bottom-2 left-0 h-0.5 w-full rounded-full bg-[#97025C] transition-transform ${
                      isActive ? "scale-100" : "scale-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="relative flex items-center gap-3">
          <button
            ref={toggleButtonRef}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="flex items-center gap-3 rounded-full p-2 bg-[#FAF7FA] text-sm font-semibold text-[#61013B] shadow-sm transition hover:border-[#97025C] hover:text-[#97025C]"
            aria-label="Open profile menu"
          >
            <Menu className="h-6 w-6 rounded" />
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 top-[calc(100%+0.75rem)] z-40 w-[320px] max-w-[90vw]">
              <div
                ref={menuRef}
                className="rounded-2xl border border-gray-100 bg-white p-4 shadow-xl shadow-[#97025C]/10"
              >
                <div className="flex flex-col items-center gap-1 pb-4 text-center">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-[#f8e9f3] text-[#97025C] shadow-sm">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={displayName}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "";
                        }}
                      />
                    ) : (
                      <span className="text-lg font-semibold">
                        {getInitials(displayName)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                  {email ? (
                    <p className="text-xs font-medium text-gray-500">
                      {email}
                    </p>
                  ) : null}
                </div>

                {isServiceEnabled("puloAssistants") && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-white md:hidden"
                    onClick={() => goTo("/user/travel-assistant")}
                  >
                    <User className="h-4 w-4 text-[#97025C]" />
                    <span>Pulo Assistant</span>
                  </button>
                )}
                {isServiceEnabled("stays") && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-white md:hidden"
                    onClick={() => goTo("/user/stays")}
                  >
                    <BedDouble className="h-4 w-4 text-[#97025C]" />
                    <span>Stays</span>
                  </button>
                )}
                {isServiceEnabled("rides") && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-white md:hidden"
                    onClick={() => goTo("/user/rides")}
                  >
                    <Car className="h-4 w-4 text-[#97025C]" />
                    <span>Rides</span>
                  </button>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-white md:hidden"
                  onClick={() => goTo("/user/trips")}
                >
                  <CalendarDays className="h-4 w-4 text-[#97025C]" />
                  <span>My Trips</span>
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-white md:hidden"
                  onClick={() => goTo("/user/messages")}
                >
                  <MessageSquare className="h-4 w-4 text-[#97025C]" />
                  <span>Messages</span>
                </button>

                <div className="space-y-2">
                  <div className="divide-y rounded-xl border border-gray-100 bg-gray-50/60">
                    {profileActions.map(({ label, to, Icon }) => (
                      <button
                        key={to}
                        type="button"
                        onClick={() => goTo(to)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-white"
                      >
                        <Icon className="h-4 w-4 text-[#97025C]" />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#6a003f] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#570033]"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Start/end confirmation for the trip the traveller is on, so they
          never have to go through My Trips to reach it. */}
      <TripActionBar />
    </header>
  );
}

export default HeaderDash;
